import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

/**
 * NotificationsTable component with filtering and search functionality
 * Implements theme-aware styling and follows existing admin table patterns
 */
const NotificationsTable = ({
  notifications,
  loading,
  selectedNotification,
  onNotificationSelect,
  onNotificationEdit,
  onNotificationDelete,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onRefresh
}) => {
  const { theme } = useTheme();
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filteredNotifications, setFilteredNotifications] = useState([]);

  // Filter and sort notifications
  useEffect(() => {
    let filtered = [...notifications];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(notification =>
        notification.title.toLowerCase().includes(searchLower) ||
        notification.body.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(notification => {
        if (statusFilter === 'active') return notification.is_active;
        if (statusFilter === 'inactive') return !notification.is_active;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle date sorting
      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredNotifications(filtered);
  }, [notifications, searchTerm, statusFilter, sortBy, sortOrder]);

  /**
   * Handle sort column click
   */
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  /**
   * Truncate text for preview
   */
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (isActive) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    if (isActive) {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
          : 'bg-green-100 text-green-800 border border-green-200'
      }`;
    } else {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-red-900/30 text-red-400 border border-red-500/30' 
          : 'bg-red-100 text-red-800 border border-red-200'
      }`;
    }
  };

  /**
   * Render sort icon
   */
  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} ml-1`}>↕️</span>;
    }
    return (
      <span className={`${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'} ml-1`}>
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } rounded-lg border overflow-hidden shadow-sm`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading notifications...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg border overflow-hidden shadow-sm`}>
      {/* Table Header */}
      <div className={`px-6 py-4 ${
        theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
      } border-b flex justify-between items-center`}>
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Notifications ({filteredNotifications.length})
        </h3>
        
        {/* Filter Controls */}
        <div className="flex items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className={`w-64 px-3 py-2 text-sm rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>🔍</span>
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            className={`px-3 py-2 text-sm rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
            title="Refresh notifications"
          >
            🔄
          </button>
        </div>
      </div>

      {/* Table Content */}
      {filteredNotifications.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-4xl mb-4">🔔</div>
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
            No notifications found
          </h3>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
            {searchTerm || statusFilter !== 'all' 
              ? 'No notifications match your current filters. Try adjusting your search criteria.'
              : 'No notifications have been created yet.'
            }
          </p>
          {(searchTerm || statusFilter !== 'all') && (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => onSearchChange('')}
                className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
              >
                Clear Search
              </button>
              <button
                onClick={() => onStatusFilterChange('all')}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors dark:text-white"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${
              theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
            } border-b`}>
              <tr>
                <th 
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                  } uppercase tracking-wider cursor-pointer transition-colors`}
                  onClick={() => handleSort('title')}
                >
                  Title
                  <SortIcon column="title" />
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } uppercase tracking-wider`}>
                  Body Preview
                </th>
                <th 
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                  } uppercase tracking-wider cursor-pointer transition-colors`}
                  onClick={() => handleSort('created_at')}
                >
                  Date
                  <SortIcon column="created_at" />
                </th>
                <th 
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                  } uppercase tracking-wider cursor-pointer transition-colors`}
                  onClick={() => handleSort('is_active')}
                >
                  Status
                  <SortIcon column="is_active" />
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } uppercase tracking-wider`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`${
              theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'
            } divide-y`}>
              {filteredNotifications.map((notification) => (
                <tr
                  key={notification.id}
                  className={`cursor-pointer transition-colors ${
                    selectedNotification?.id === notification.id
                      ? theme === 'dark'
                        ? 'bg-teal-900/20 border-l-4 border-l-teal-400'
                        : 'bg-teal-50 border-l-4 border-l-teal-500'
                      : theme === 'dark'
                        ? 'hover:bg-gray-700/50'
                        : 'hover:bg-gray-50'
                  }`}
                  onClick={() => onNotificationSelect(notification)}
                >
                  <td className="px-6 py-4">
                    <div className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {notification.title}
                    </div>
                    {notification.current_revision > 1 && (
                      <div className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      } mt-1`}>
                        Revision {notification.current_revision}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {truncateText(notification.body)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {formatDate(notification.created_at)}
                    </div>
                    {notification.updated_at !== notification.created_at && (
                      <div className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      } mt-1`}>
                        Updated: {formatDate(notification.updated_at)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={getStatusBadge(notification.is_active)}>
                      {notification.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onNotificationSelect(notification);
                        }}
                        className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                      >
                        View Details
                      </button>
                      <span className={`${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`}>|</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onNotificationEdit(notification);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <span className={`${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`}>|</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onNotificationDelete(notification);
                        }}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationsTable;
